import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FiTarget, FiCalendar } from 'react-icons/fi';
import { GoLocation } from 'react-icons/go';
import { MdArrowDropDown } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { ENV } from '@environment';
import { isEqual } from 'lodash';

import { flightsSearchParameters } from 'action-creators/flights';
import Checkbox from 'components/Checkbox';
import DateRangePicker from 'components/DateRangePicker';
import BookingSelectDate, { RenderProps as BookingSelectRenderProps } from 'components/Selects/BookingSelectDate';
import BookingSelectLocation from 'components/Selects/BookingSelectLocation';
import BookingSelectTime from 'components/Selects/BookingSelectTime';
import BranchCompanySelect from 'components/Selects/BranchCompanySelect';
import TravelArrangerSelect from 'components/Selects/TravelArrangerSelect';
import AirportSearch from 'containers/AirportsSearch';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { Airport } from 'models/airport';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';
import { GroupedListModel } from 'models/groupedList';
import ResponseScreen, { BackgroundTypes, IconTypes } from 'screens/components/ResponseScreen';
import { amplitudelogEvent, SearchEvent } from 'services/amplitudeService';
import { Guest } from 'services/userGuestService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { dateFormatWeekend, daysCount } from 'utils/generic';
import { compareDates } from 'utils/time';

import FlightClassSelector from './components/FlightClassSelector';
import FlightTimePicker, { Props as FlightTimePickerProps } from './components/FlightTimePicker';
import { TimeOption } from './components/FlightTimePicker/models';
import {
  Container,
  Fields,
  Field,
  SearchButton,
  TravelKindRowContainer,
  Checkbox as CheckboxContainer,
  BaggageIncluded,
  SearchButtonContainer,
} from './styles';

interface Props {
  defaultTraveler?: TravelArrangerUsersAllowed;
  defaultDeparturePoint?: Airport;
  defaultArrivalPoint?: Airport;
  defaultDepartureDate?: Date;
  defaultDepartureTime?: TimeOption;
  defaultReturnDate?: Date;
  defaultReturnTime?: TimeOption;
  defaultCabin?: string;
  defaultBaggageIncluded: boolean;
  defaultDirectFlight: boolean;
  allowedTravelers: TravelArrangerUsersAllowed[];
  isLoadingAllowedTravelers: boolean;
  isHome?: boolean;
  onClose?: () => void;
  branchCompanies?: GetAllBillingCompanyResponse[];
  isLoadingBranchCompanies: boolean;
  defaultBranchCompany?: GetAllBillingCompanyResponse;
}

export const getBookingSelectValue = (airport: Airport | undefined) => {
  if (!airport) return;

  return {
    name: airport.code,
    detail: `${airport.related_city}, ${airport.country}`,
  };
};

const travelerToGroupedModel = (traveler: TravelArrangerUsersAllowed): GroupedListModel => ({
  id: traveler.id,
  name: traveler.name,
  lastName: traveler.lastName,
  favorite: traveler.favorite,
  email: traveler.email,
  userGroupId: traveler.userGroupId,
  userGroupName: traveler.userGroupName,
  entity: traveler.guest ? 'guest' : 'user',
});

const branchCompanyToGroupedModel = (branchCompany: GetAllBillingCompanyResponse): GroupedListModel => ({
  id: branchCompany.id,
  name: branchCompany.corporateName,
});

const FlightsSearch = ({
  defaultTraveler,
  defaultDeparturePoint,
  defaultArrivalPoint,
  defaultDepartureDate,
  defaultDepartureTime,
  defaultReturnDate,
  defaultReturnTime,
  defaultCabin,
  defaultBaggageIncluded,
  defaultDirectFlight,
  allowedTravelers,
  isLoadingAllowedTravelers,
  isHome = true,
  onClose,
  branchCompanies,
  isLoadingBranchCompanies,
  defaultBranchCompany,
}: Props) => {
  const history = useHistory();
  const location = useLocation<{ showNoResults?: boolean } | null>();
  const { t } = useStoreTranslation();
  const user = useUserAccount();
  const dispatch = useDispatch();

  const FLIGHT_KIND = [
    { label: t('flightsSearch.type.roundTrip'), value: true, testId: 'flight-search-round-trip' },
    { label: t('flightsSearch.type.oneWay'), value: false, testId: 'flight-search-one-way' },
  ];

  const [showNoResults, setShowNoResults] = useState(location.state?.showNoResults || false);
  const [roundTrip, setRoundTrip] = useState<boolean>(!(defaultDeparturePoint && !defaultReturnDate));
  const [departurePoint, setDeparturePoint] = useState<Airport | undefined>(defaultDeparturePoint);
  const [arrivalPoint, setArrivalPoint] = useState<Airport | undefined>(defaultArrivalPoint);
  const [departureDate, setDepartureDate] = useState<Date | undefined>(defaultDepartureDate);
  const [departureTime, setDepartureTime] = useState<TimeOption | undefined>(defaultDepartureTime);
  const [cabin, setCabin] = useState<string | undefined>(defaultCabin);
  const [returnDate, setReturnDate] = useState<Date | undefined>(defaultReturnDate);
  const [returnTime, setReturnTime] = useState<TimeOption | undefined>(defaultReturnTime);
  const [traveler, setTraveler] = useState<TravelArrangerUsersAllowed | undefined>(defaultTraveler);
  const [baggageIncluded, setBaggageIncluded] = useState<boolean>(defaultBaggageIncluded);
  const [directFlight, setDirectFlight] = useState<boolean>(defaultDirectFlight);
  const [branchCompany, setBranchCompany] = useState<GetAllBillingCompanyResponse | undefined>(defaultBranchCompany);

  useEffect(() => {
    history.replace({ ...history.location, state: undefined });
  }, [history]);

  useEffect(() => {
    setTraveler(defaultTraveler);
  }, [defaultTraveler]);

  useEffect(() => {
    setBranchCompany(defaultBranchCompany);
  }, [defaultBranchCompany]);

  const isValid = useMemo(() => {
    const hasTraveler = !user?.isTravelArranger || (user?.isTravelArranger && traveler);
    if (roundTrip) {
      return Boolean(departurePoint && arrivalPoint && hasTraveler && departureDate && returnDate);
    }
    return Boolean(departurePoint && arrivalPoint && hasTraveler && departureDate);
  }, [user, traveler, roundTrip, departurePoint, arrivalPoint, departureDate, returnDate]);

  const isBranchCompanyValid = useMemo(() => {
    if (branchCompanies && branchCompanies.length > 0) {
      if (branchCompany === undefined) {
        return false;
      }
    }
    return true;
  }, [branchCompanies, branchCompany]);

  const transformedTravelerList: GroupedListModel[] = useMemo(() => allowedTravelers.map(travelerToGroupedModel), [
    allowedTravelers,
  ]);
  const transformedSelectedTraveler: GroupedListModel | undefined = useMemo(() => {
    if (!traveler) return undefined;
    return travelerToGroupedModel(traveler);
  }, [traveler]);

  const transformedSelectedBranchCompany: GroupedListModel | undefined = useMemo(() => {
    if (!branchCompany) return undefined;
    return branchCompanyToGroupedModel(branchCompany);
  }, [branchCompany]);

  const handleChangeRoundTrip = (value: boolean) => {
    if (value === roundTrip) return;
    setRoundTrip(value);
    setReturnDate(undefined);
    setReturnTime(undefined);
  };

  const handleChangeDateSelect = (dates: { initialDate: Date; finalDate: Date }) => {
    setDepartureDate(dates?.initialDate);
    setReturnDate(dates?.finalDate);
  };

  const cabinCodes = [
    { text: t('flightsSearch.cabin.economy'), value: 'Economy' },
    { text: t('flightsSearch.cabin.premiumEconomy'), value: 'PremiumEconomy' },
    { text: t('flightsSearch.cabin.business'), value: 'Business' },
    { text: t('flightsSearch.cabin.first'), value: 'First' },
  ];

  const handleClickSearchButton = useCallback(async () => {
    if (departurePoint && departureDate && arrivalPoint && cabin) {
      if (
        baggageIncluded === defaultBaggageIncluded &&
        directFlight === defaultDirectFlight &&
        isEqual(departurePoint, defaultDeparturePoint) &&
        isEqual(arrivalPoint, defaultArrivalPoint) &&
        cabin === defaultCabin &&
        roundTrip === !(defaultDeparturePoint && !defaultReturnDate) &&
        compareDates(departureDate, defaultDepartureDate) &&
        compareDates(returnDate, defaultReturnDate) &&
        isEqual(traveler, defaultTraveler) &&
        isEqual(departureTime, defaultDepartureTime) &&
        isEqual(returnTime, defaultReturnTime) &&
        !isHome
      ) {
        if (onClose) onClose();
        return;
      }
      await dispatch(
        flightsSearchParameters({
          baggageIncluded,
          departurePoint,
          departureDate,
          arrivalPoint,
          cabin,
          returnDate,
          traveler,
          departureTime,
          returnTime,
          directFlight,
          branchCompany,
        }),
      );

      if (isHome) history.push('/flights');
      else history.replace('/flights');
    }

    //Amplitude
    const event: SearchEvent = {
      TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
      TRAVEL_FROM: departurePoint?.code ?? '',
      TRAVEL_TO: arrivalPoint?.code ?? '',
      TRAVEL_DESTINATION_TYPE: '',
      TRAVEL_TRIP_TYPE: roundTrip ? 'true' : 'false',
      TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
      TRAVEL_DATE_FROM: departureDate ? new Date(departureDate?.toString()).toString() : '',
      TRAVEL_DATE_TO: returnDate ? new Date(returnDate?.toString()).toString() : '',
      TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
      TRAVEL_PASSENGERS: '1',
      TRAVEL_ADULTS: '1',
      TRAVEL_CHILDREN: '0',
      TRAVEL_INFANTS: '0',
      TRAVEL_CLASS_OF_SERVICE: cabin ?? '',
      TRAVEL_ROOMS: '',
      TRAVEL_WITH_BAGGAGE: baggageIncluded ? 'true' : 'false',
      TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
      SEARCHID: '',
    };

    amplitudelogEvent(amplitudeEvents.SELECT_TRAVEL_SEARCH, event, user);

    history.push('/flights', {
      baggageIncluded,
      departurePoint,
      departureDate,
      arrivalPoint,
      cabin,
      roundTrip,
      returnDate,
      traveler,
      branchCompany,
    });
  }, [
    arrivalPoint,
    baggageIncluded,
    cabin,
    defaultArrivalPoint,
    defaultBaggageIncluded,
    defaultCabin,
    defaultDepartureDate,
    defaultDeparturePoint,
    defaultDepartureTime,
    defaultReturnDate,
    defaultReturnTime,
    defaultTraveler,
    departureDate,
    departurePoint,
    departureTime,
    dispatch,
    history,
    isHome,
    onClose,
    returnDate,
    returnTime,
    roundTrip,
    traveler,
    user,
    directFlight,
    defaultDirectFlight,
    branchCompany,
  ]);

  const MULTI_DESTINATION_INDEX = 2;

  const transformedBranchCompanies = (): GroupedListModel[] =>
    branchCompanies ? branchCompanies?.map((b) => ({ id: b.id, name: b.corporateName })) : ([] as GroupedListModel[]);

  const renderRoundTripSelect = () => (
    <TravelKindRowContainer>
      {FLIGHT_KIND.map((flightKind, index) => (
        <TravelKindRowContainer.CheckboxContainer key={index.toString()}>
          <Checkbox
            circle
            checked={index === MULTI_DESTINATION_INDEX ? false : roundTrip === flightKind.value}
            onChange={() => (index === MULTI_DESTINATION_INDEX ? {} : handleChangeRoundTrip(flightKind.value))}
            testId={flightKind.testId}
          />
          <TravelKindRowContainer.label>{flightKind.label}</TravelKindRowContainer.label>
        </TravelKindRowContainer.CheckboxContainer>
      ))}
    </TravelKindRowContainer>
  );

  const renderDeparturePointSelect = () => (
    <BookingSelectLocation
      label={t('flightsSearch.departurePoint.label')}
      detail={t('flightsSearch.departurePoint.detail')}
      value={getBookingSelectValue(departurePoint)}
      onChange={(value, isCity?: boolean) => {
        setDeparturePoint(value as Airport);
      }}
      icon={<FiTarget />}
      testId="flight-search-departure-location"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <AirportSearch
          type="DEPARTURE"
          {...selectProps}
          placeHolder={t('flightsSearch.departurePoint.modalSearchPlaceholder')}
        />
      )}
    </BookingSelectLocation>
  );

  const renderArrivalPointSelect = () => (
    <BookingSelectLocation
      label={t('flightsSearch.arrivalPoint.label')}
      detail={t('flightsSearch.arrivalPoint.detail')}
      value={getBookingSelectValue(arrivalPoint)}
      onChange={(value, isCity?: boolean) => {
        setArrivalPoint(value as Airport);
      }}
      icon={<GoLocation />}
      testId="flight-search-arrival-location"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <AirportSearch
          type="ARRIVAL"
          {...selectProps}
          placeHolder={t('flightsSearch.arrivalPoint.modalSearchPlaceholder')}
        />
      )}
    </BookingSelectLocation>
  );

  const renderDepartureDateSelect = () => (
    <BookingSelectDate
      label={t('flightsSearch.tripDate.departure.label')}
      value={dateFormatWeekend(departureDate)}
      onChange={handleChangeDateSelect}
      icon={<FiCalendar />}
      testId="flight-search-departure-date"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <DateRangePicker
          {...selectProps}
          type="trip"
          onlyDate={!roundTrip}
          currentInitialDate={departureDate}
          currentFinalDate={returnDate}
        />
      )}
    </BookingSelectDate>
  );

  const handleChangeDepartureTimeSelect = useCallback(
    (time) => {
      if (time === undefined) {
        setDepartureTime(undefined);
        return;
      }
      if (time.indexOf('AM') > -1 || time.indexOf('PM') > -1) {
        setDepartureTime({ label: time, value: time });
        return;
      }
      setDepartureTime({ label: t(`flightsSearch.tripTime.${time}`), value: time });
    },
    [t],
  );

  const isDepartureEnabled = useMemo(() => departureDate === undefined, [departureDate]);
  const renderDepartureTimeSelect = () => (
    <BookingSelectTime
      label={t('flightsSearch.tripDate.departure.title')}
      placeholder={t('flightsSearch.tripDate.departure.placeholder')}
      value={departureTime?.label}
      disabled={isDepartureEnabled}
      onConfirm={handleChangeDepartureTimeSelect}
      icon={<MdArrowDropDown />}
      testId="flight-search-departure-time"
      title={t('flightsSearch.tripDate.departure.title')}
      button={t('flightsSearch.tripDate.departure.button')}
    >
      {(selectProps: FlightTimePickerProps) => <FlightTimePicker {...selectProps} selected={departureTime?.value} />}
    </BookingSelectTime>
  );

  const renderReturnDateSelect = () => (
    <BookingSelectDate
      disabled={!roundTrip}
      label={t('flightsSearch.tripDate.return.label')}
      value={dateFormatWeekend(returnDate)}
      onChange={handleChangeDateSelect}
      icon={<FiCalendar />}
      testId="flight-search-return-date"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <DateRangePicker
          {...selectProps}
          type="trip"
          onlyDate={false}
          currentInitialDate={departureDate}
          currentFinalDate={returnDate}
          editingInitialDate={false}
        />
      )}
    </BookingSelectDate>
  );

  const handleChangeReturnTimeSelect = useCallback(
    (time) => {
      if (time === undefined) {
        setReturnTime(undefined);
        return;
      }
      if (time.indexOf('AM') > -1 || time.indexOf('PM') > -1) {
        setReturnTime({ label: time, value: time });
        return;
      }
      setReturnTime({ label: t(`flightsSearch.tripTime.${time}`), value: time });
    },
    [t],
  );

  const isReturnEnabled = useMemo(() => returnDate === undefined, [returnDate]);
  const renderReturnTimeSelect = () => (
    <BookingSelectTime
      label={t('flightsSearch.tripDate.return.title')}
      placeholder={t('flightsSearch.tripDate.return.placeholder')}
      value={returnTime?.label}
      disabled={isReturnEnabled}
      onConfirm={handleChangeReturnTimeSelect}
      icon={<MdArrowDropDown />}
      testId="flight-search-return-time"
      title={t('flightsSearch.tripDate.return.title')}
      button={t('flightsSearch.tripDate.return.button')}
    >
      {(selectProps: FlightTimePickerProps) => <FlightTimePicker {...selectProps} selected={returnTime?.value} />}
    </BookingSelectTime>
  );

  const renderClassSelect = () => {
    const flightClassOptions = cabinCodes.map((option) => ({
      id: option.value,
      name: option.text,
    }));

    return (
      <FlightClassSelector
        value={cabin}
        onChange={setCabin}
        options={flightClassOptions}
        title={t('flightsSearch.cabin.title')}
        testId="flight-search-class"
      />
    );
  };

  const renderOptionsDesktop = () => (
    <>
      <Field columns={4}>
        <Field></Field>
        <Field></Field>
        <CheckboxContainer
          checked={directFlight}
          onChange={() => setDirectFlight(!directFlight)}
          testId="flight-search-direct-flight"
        >
          <BaggageIncluded onClick={() => setDirectFlight(!directFlight)}>
            {t('flightsSearch.directFlight')}
          </BaggageIncluded>
        </CheckboxContainer>

        <CheckboxContainer
          checked={baggageIncluded}
          onChange={() => setBaggageIncluded(!baggageIncluded)}
          testId="flight-search-only-baggage-included"
        >
          <BaggageIncluded onClick={() => setBaggageIncluded(!baggageIncluded)}>
            {t('flightsSearch.baggageIncluded')}
          </BaggageIncluded>
        </CheckboxContainer>
      </Field>
    </>
  );

  const renderOptionsMobile = () => (
    <Field>
      <CheckboxContainer
        checked={directFlight}
        onChange={() => setDirectFlight(!directFlight)}
        testId="flight-search-direct-flight"
      >
        <BaggageIncluded onClick={() => setDirectFlight(!directFlight)}>
          {t('flightsSearch.directFlight')}
        </BaggageIncluded>
      </CheckboxContainer>

      <CheckboxContainer
        checked={baggageIncluded}
        onChange={() => setBaggageIncluded(!baggageIncluded)}
        testId="flight-search-only-baggage-included"
      >
        <BaggageIncluded onClick={() => setBaggageIncluded(!baggageIncluded)}>
          {t('flightsSearch.baggageIncluded')}
        </BaggageIncluded>
      </CheckboxContainer>
    </Field>
  );

  const handleTravelerSelect = (item: GroupedListModel) => {
    setTraveler(allowedTravelers.find((traveler) => traveler.id === item.id));
  };

  const handleBranchCompanySelect = (item: GroupedListModel) => {
    if (branchCompanies) {
      setBranchCompany(branchCompanies?.find((branchCompany) => branchCompany.id === item.id));
    }

    return;
  };

  const handleCreateGuestTraveler = (guest: Guest) => {
    setTraveler({
      guest: true,
      favorite: false,
      id: guest.id || '',
      userId: guest.userId || '',
      name: guest.name,
      lastName: guest.lastName,
      email: guest.email,
      birthDate: guest.birthDate,
      phoneNumber: guest.phoneNumber,
      country: guest.country,
      documentNumber: guest.documentNumber,
      gender: guest.gender,
      passportNumber: guest.passportNumber,
    });
  };

  const isMobile = useIsMobile();

  const renderTravelerSelect = () => {
    if (!user) return;
    if (!user.isTravelArranger) return null;

    return (
      <TravelArrangerSelect
        title={t('travelArrangerSelector.placeholder')}
        buttonLabel={t('travelArrangerSelector.buttonLabel')}
        addGuestLabel={t('travelArrangerSelector.addGuest')}
        inviteAllowed={user.isGuestBookAllowed}
        options={transformedTravelerList}
        value={transformedSelectedTraveler}
        onChange={handleTravelerSelect}
        onCreateGuest={handleCreateGuestTraveler}
        isLoading={isLoadingAllowedTravelers}
        testId="flight-search-traveler"
      />
    );
  };

  const renderBranchCompanySelect = () => {
    if (!user) return;
    if (!branchCompanies) return;
    if (branchCompanies.length <= 0) return;

    return (
      <BranchCompanySelect
        title={t('billingCompanySelector.placeholder')}
        buttonLabel={t('billingCompanySelector.buttonLabel')}
        options={transformedBranchCompanies()}
        value={transformedSelectedBranchCompany}
        onChange={handleBranchCompanySelect}
        isLoading={isLoadingBranchCompanies}
        testId="hotel-search-branch-company"
      />
    );
  };

  return (
    <>
      <Container>
        <Fields>
          <Field>{renderRoundTripSelect()}</Field>
          <Field>{renderDeparturePointSelect()}</Field>
          <Field>{renderArrivalPointSelect()}</Field>
          <Field columns={2}>
            {renderDepartureDateSelect()} {renderDepartureTimeSelect()}
          </Field>
          <Field columns={2}>
            {renderReturnDateSelect()} {renderReturnTimeSelect()}
          </Field>
          <Field>{renderClassSelect()}</Field>
          <Field>{renderTravelerSelect()}</Field>
          <Field>{renderBranchCompanySelect()}</Field>
          {isMobile ? renderOptionsMobile() : renderOptionsDesktop()}
        </Fields>
        <SearchButtonContainer>
          <SearchButton
            disabled={!isValid || isLoadingAllowedTravelers || !isBranchCompanyValid}
            onClick={handleClickSearchButton}
            value={t('flightsSearch.searchButton')}
            testId="flight-search-submit"
          />
        </SearchButtonContainer>
      </Container>

      <ResponseScreen
        show={showNoResults}
        text="Ops!"
        subText={t('flightsSearch.searchError')}
        icon={IconTypes.Airplane}
        background={BackgroundTypes.Cloud}
        buttonOnClick={() => setShowNoResults(false)}
        buttonText={t('flightsSearch.backButton')}
      />
    </>
  );
};

export default FlightsSearch;
